import React, { useState } from "react";
import { useSearchParams } from "react-router-dom";
import { Button } from "../ui/button";
import { Building2, User } from "lucide-react";
import { ThemeToggle } from "../ui/theme-toggle";
import { useTheme } from "../../contexts/ThemeContext";
import MaitaiLogo from "../../assets/maitai_logo_full.svg";
import MaitaiLogoDark from "../../assets/maitai_logo_dark_transparent.svg";

const LoginChooser: React.FC = () => {
  const [searchParams] = useSearchParams();
  const [isSignUp, setIsSignUp] = useState(false);
  const [isRedirecting, setIsRedirecting] = useState(false);
  const { theme } = useTheme();

  const startLogin = (
    mode: "org" | "individual",
    action: "signin" | "signup"
  ) => {
    sessionStorage.setItem("auth_inflight_ts", Date.now().toString());
    setIsRedirecting(true);
    if (mode === "individual") {
      localStorage.removeItem("auth0_org_id");
    }
    localStorage.setItem("auth_mode", mode);
    localStorage.removeItem("switching_org");
    const params = new URLSearchParams(searchParams);
    params.set("start", "1");
    params.set("auth_action", action);
    window.location.href = `/login?${params.toString()}`;
  };

  const startOrgSignup = () => {
    sessionStorage.setItem("auth_inflight_ts", Date.now().toString());
    setIsRedirecting(true);
    localStorage.setItem("auth_mode", "org");
    localStorage.removeItem("auth0_org_id");
    localStorage.removeItem("switching_org");
    window.location.href = "/org-signup";
  };

  if (isRedirecting) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="space-y-3 text-center">
          <p className="text-lg font-semibold text-foreground">Loading...</p>
          <p className="text-sm text-muted-foreground">
            One moment while we finish authentication.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-background relative">
      <div className="absolute top-4 left-4">
        <img
          src={theme === "dark" ? MaitaiLogoDark : MaitaiLogo}
          alt="Maitai"
          className="h-8 w-auto"
        />
      </div>
      <div className="absolute top-4 right-4">
        <ThemeToggle />
      </div>
      <div className="w-full max-w-3xl space-y-8 p-8">
        <div className="space-y-2 text-center">
          <h1 className="text-3xl font-gc-demi-bold text-foreground">
            {isSignUp ? "Sign Up" : "Sign In"}
          </h1>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="border rounded-xl p-8 flex flex-col items-center text-center gap-4 bg-card">
            <div className="h-14 w-14 rounded-lg bg-primary/10 flex items-center justify-center">
              <Building2 className="h-8 w-8 text-primary" />
            </div>
            <div className="space-y-1">
              <p className="text-xl font-semibold text-foreground">
                Organization
              </p>
            </div>
            <Button
              className="w-full"
              onClick={() =>
                isSignUp ? startOrgSignup() : startLogin("org", "signin")
              }>
              {isSignUp
                ? "Sign up as organization"
                : "Continue as organization"}
            </Button>
          </div>

          <div className="border rounded-xl p-8 flex flex-col items-center text-center gap-4 bg-card">
            <div className="h-14 w-14 rounded-lg bg-muted flex items-center justify-center">
              <User className="h-8 w-8 text-foreground" />
            </div>
            <div className="space-y-1">
              <p className="text-xl font-semibold text-foreground">
                Individual
              </p>
            </div>
            <Button
              className="w-full"
              onClick={() =>
                startLogin("individual", isSignUp ? "signup" : "signin")
              }>
              {isSignUp ? "Sign up as individual" : "Continue as individual"}
            </Button>
          </div>
        </div>
        <div className="text-center text-sm text-muted-foreground">
          {isSignUp ? "Already have an account?" : "Don't have an account?"}
          <button
            style={{ marginLeft: 4 }}
            className="text-primary underline"
            onClick={() => setIsSignUp(!isSignUp)}>
            {isSignUp ? "Sign in" : "Sign up"}
          </button>
        </div>
      </div>
    </div>
  );
};

export default LoginChooser;
